import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { LockKeyhole, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ADMIN_PASSWORD, AUTH_KEY } from '@/lib/adminAuth'

export function Login() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'robots'
      document.head.appendChild(meta)
    }
    meta.content = 'noindex, nofollow'
    return () => {
      meta?.remove()
    }
  }, [])

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError(true)
      return
    }
    setIsSubmitting(true)
    setError(false)

    setTimeout(() => {
      if (password === ADMIN_PASSWORD) {
        localStorage.setItem(AUTH_KEY, '1')
        navigate(`/${lang}/area-riservata`)
      } else {
        setError(true)
      }
      setIsSubmitting(false)
    }, 400)
  }

  return (
    <section className="flex min-h-screen items-center justify-center bg-maia px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-white md:text-5xl">
            {t('login.hero.title')}
          </h1>
          <p className="mx-auto mt-4 max-w-sm text-base text-white/80 md:text-lg">
            {t('login.hero.subtitle')}
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-cord bg-white p-8 shadow-sm md:p-10">
          <div className="mb-6 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-maia/10 text-maia">
              <LockKeyhole className="h-6 w-6" aria-hidden="true" />
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" noValidate>
            <div>
              <label
                htmlFor="admin-username"
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted"
              >
                {t('login.form.username')}
              </label>
              <input
                id="admin-username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value)
                  if (error) setError(false)
                }}
                disabled={isSubmitting}
                className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20 disabled:opacity-50"
                placeholder={t('login.form.username')}
              />
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted"
              >
                {t('login.form.password')}
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError(false)
                }}
                disabled={isSubmitting}
                className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20 disabled:opacity-50"
                placeholder={t('login.form.password')}
              />
            </div>

            {error && (
              <p className="text-sm font-medium text-pizza" role="alert">
                {t('login.error.invalidCredentials')}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'w-full rounded-full bg-maia px-8 py-4 text-sm font-bold text-white shadow-lg transition-colors hover:bg-maia-dark disabled:cursor-not-allowed disabled:opacity-70'
              )}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('login.form.submitting')}
                </span>
              ) : (
                t('login.form.submit')
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}
